"use client";
import { useEffect, useState } from "react";

export interface DeviceCapability {
  /** True for genuinely constrained devices (low RAM, data-saver, or slow CPU on a slow network). */
  isLowEnd: boolean;
  /** The user has Chrome's Data Saver / "Lite mode" enabled. */
  saveData: boolean;
}

type NavigatorWithCaps = Navigator & {
  deviceMemory?: number;
  connection?: { saveData?: boolean; effectiveType?: string };
};

const SAFE_DEFAULT: DeviceCapability = { isLowEnd: false, saveData: false };

function detect(): DeviceCapability {
  if (typeof navigator === "undefined") return SAFE_DEFAULT;

  const nav = navigator as NavigatorWithCaps;
  const conn = nav.connection;

  const saveData = conn?.saveData === true;
  const effectiveType = conn?.effectiveType;
  const slowNet =
    effectiveType === "slow-2g" || effectiveType === "2g" || effectiveType === "3g";

  // deviceMemory is coarse (0.25 / 0.5 / 1 / 2 / 4 / 8 GB) and Chromium-only — which is
  // exactly our Android-Chrome target. <=2 GB is the strongest "low-end" signal.
  const lowMem = typeof nav.deviceMemory === "number" && nav.deviceMemory <= 2;
  // Cores alone over-triggers (many mid phones report 8), so only count it together with a slow network.
  const lowCpu =
    typeof nav.hardwareConcurrency === "number" && nav.hardwareConcurrency <= 4;

  const isLowEnd = lowMem || saveData || (lowCpu && slowNet);
  return { isLowEnd, saveData };
}

/**
 * Reports device capability so the heaviest work (session recording, carousel autoplay…)
 * can be skipped on constrained devices.
 *
 * SSR-safe: returns the "capable" default on the server and the first client render, then
 * refines to the real value after mount — so it never causes a hydration mismatch. Consumers
 * should therefore treat `isLowEnd` as "becomes true shortly after load" rather than relying
 * on it during the very first paint.
 */
export function useDeviceCapability(): DeviceCapability {
  const [cap, setCap] = useState<DeviceCapability>(SAFE_DEFAULT);

  useEffect(() => {
    setCap(detect());
  }, []);

  return cap;
}

export default useDeviceCapability;
